import { useEffect, useState } from "react";
import { MailCheck, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const hourLabel = (h) => `${h % 12 === 0 ? 12 : h % 12}:00 ${h < 12 ? "AM" : "PM"}`;

export function WeekAheadButton() {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const send = async () => {
    setBusy(true);
    try { const r = await api.get("/digest/week-ahead"); await api.post("/digest/week-ahead/send"); toast.success(`Week-ahead digest emailed: ${r.data.court.length} court date${r.data.court.length === 1 ? "" : "s"}, ${r.data.appointments.length} appointment${r.data.appointments.length === 1 ? "" : "s"}`); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center">
      <Button variant="outline" disabled={busy} className="border-[#2E3B61] text-slate-200 hover:bg-[#1C2541] rounded-r-none" onClick={send} data-testid="week-ahead-send-button"><MailCheck className="w-4 h-4 mr-2" /> {busy ? "Sending…" : "Email my week ahead"}</Button>
      <Button variant="outline" className="border-[#2E3B61] border-l-0 text-slate-300 hover:bg-[#1C2541] rounded-l-none px-2" onClick={() => setOpen(true)} title="Digest preferences" data-testid="digest-prefs-button"><Settings2 className="w-4 h-4" /></Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F1A36] border-[#2E3B61] text-slate-100 max-w-md" data-testid="digest-prefs-dialog">
          <DialogHeader><DialogTitle className="font-serif text-xl">Week-ahead digest</DialogTitle>
            <DialogDescription className="text-slate-400">Choose when your digest of next week's court dates, deadlines and appointments arrives.</DialogDescription></DialogHeader>
          {open && <DigestPrefsForm onDone={() => setOpen(false)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DigestPrefsForm({ onDone }) {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/digest/preferences").then((r) => setP(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  const save = async () => {
    setBusy(true);
    try { await api.put("/digest/preferences", { enabled: p.enabled, day: p.day, hour: p.hour }); toast.success(p.enabled ? `Digest will arrive every ${DAYS[p.day]} at ${hourLabel(p.hour)}` : "Week-ahead digest paused"); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!p) return <p className="text-sm text-slate-400">Loading…</p>;
  return (
    <div className="space-y-4">
      <label className="flex items-center justify-between p-3 rounded-md border border-[#2E3B61] bg-[#0B132B] cursor-pointer">
        <span className="text-sm">Send me a weekly digest</span>
        <input type="checkbox" checked={p.enabled} onChange={(e) => setP({ ...p, enabled: e.target.checked })} className="w-4 h-4 accent-[var(--brand)]" data-testid="digest-enabled-checkbox" />
      </label>
      <div className={`grid grid-cols-2 gap-3 ${p.enabled ? "" : "opacity-40 pointer-events-none"}`}>
        <div><label className="block text-xs font-semibold text-slate-400 mb-1.5">Day</label>
          <select className="field" value={p.day} onChange={(e) => setP({ ...p, day: Number(e.target.value) })} data-testid="digest-day-select">{DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}</select></div>
        <div><label className="block text-xs font-semibold text-slate-400 mb-1.5">Time ({p.timezone})</label>
          <select className="field" value={p.hour} onChange={(e) => setP({ ...p, hour: Number(e.target.value) })} data-testid="digest-hour-select">{Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{hourLabel(h)}</option>)}</select></div>
      </div>
      <div className="flex items-center justify-between pt-2 border-t border-[#2E3B61]/50">
        <p className="text-[11px] text-slate-500" data-testid="digest-last-sent">{p.last_sent_on ? `Last sent ${p.last_sent_on}` : "Not sent yet"}</p>
        <Button size="sm" disabled={busy} onClick={save} className="bg-[var(--brand)] text-[#0B132B] hover:bg-[var(--brand-hover)]" data-testid="digest-prefs-save-button">{busy ? "Saving…" : "Save preferences"}</Button>
      </div>
    </div>
  );
}
